import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import Backdrop from "../../Components/UI/Backdrop/Backdrop";
import Card from "../../Components/common/Card/Card";
import DashBoardTable from "../../Components/PagesComponents/Dashboard/DashBoardTable/DashBoardTable";
import DemoPopup from "../../Components/common/DemoPopup/DemoPopup";
import FileCard from "../../Components/PagesComponents/Dashboard/FileCard/FileCard";
import ListPopup from "../../Components/PagesComponents/Dashboard/ListPopup/ListPopup";
import MalwareDistribution from "../../Components/PagesComponents/Dashboard/MalwareDistribution/MalwareDistribution";
import MalwareStats from "../../Components/PagesComponents/Dashboard/MalwareStats/MalwareStats";
import SystemHealth from "../../Components/PagesComponents/Dashboard/SystemHealth/SystemHealth";
import ContainerServices from "../../Components/PagesComponents/Dashboard/ContainerServices/ContainerServices";
import FreeRun from "../../Components/PagesComponents/Dashboard/FreeRun/FreeRun";

import ChartLineIcon from "../../Components/Icon-SVG/ChartLineIcon";
import MenIcon from "../../Components/Icon-SVG/MenIcon";

import {
  RequestDailySubmissions,
  RequestAllSubmissions,
  RequestIntegrationSubmissionCount,
  RequestDailyIntegrationSubmissionCount,
  RequestCleanSubmissionsState,
  RequestSuspiciousSubmissionsState,
  RequestMaliciousSubmissionsState,
  RequestIntegrationSubmissionState,
  RequestVms,
  RequestAptCount,
  RequestMalwareNameCounts,
  RequestIntegrationMalwareNameCounts,
  RequestUniqueSubmissionsCount,
} from "../../store/actions/dashboardActions";
import { licenceCheckRequest } from "../../store/actions/usersActions";
import { RequestBackendServiceConnected } from "../../store/actions/containerActions";

import "./styles.scss";

const selectState = (activeTab, submissionsState, integrationsState) => {
  if (activeTab === "all" || activeTab === "all_daily") {
    return submissionsState;
  } else if (activeTab === "integrations" || activeTab === "integrations_daily") {
    return integrationsState;
  } else {
    return 0;
  }
};

//GET FILE ID IN URL
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const allSubmissionsCount = useSelector((state) => state.dashboard.totalSubmission);
  const dailySubmissionsCount = useSelector((state) => state.dashboard.dailyCounter);
  const uniqueSubmissionsCount = useSelector((state) => state.dashboard.uniqueSubmissionsCount);
  const dailyAptCount = useSelector((state) => state.dashboard.dailyAptCounter);
  const integrationSubmissionsCount = useSelector(
    (state) => state.dashboard.integrationSubmissionCount,
  );
  const dailyIntegrationSubmissionsCount = useSelector(
    (state) => state.dashboard.dailyIntegrationSubmissionCount,
  );
  const aptCount = useSelector((state) => state.dashboard.aptCount);
  const submissionsCleanState = useSelector((state) => state.dashboard.cleanState);
  const submissionsCleanCount = useSelector((state) => state.dashboard.cleanCount);
  const submissionsSuspiciousState = useSelector((state) => state.dashboard.suspiciousState);
  const submissionsSuspiciousCount = useSelector((state) => state.dashboard.suspiciousCount);
  const submissionsMaliciousState = useSelector((state) => state.dashboard.maliciousState);
  const submissionsMaliciousCount = useSelector((state) => state.dashboard.maliciousCount);
  const integrationsCleanState = useSelector((state) => state.dashboard.integrationCleanState);
  const integrationsCleanCount = useSelector((state) => state.dashboard.integrationCleanCount);
  const integrationsSuspiciousState = useSelector(
    (state) => state.dashboard.integrationSuspiciousState,
  );
  const integrationsSuspiciousCount = useSelector(
    (state) => state.dashboard.integrationSuspiciousCount,
  );
  const integrationsMaliciousState = useSelector(
    (state) => state.dashboard.integrationMaliciousState,
  );
  const integrationsMaliciousCount = useSelector(
    (state) => state.dashboard.integrationMaliciousCount,
  );
  const submissionsLoading = useSelector((state) => state.dashboard.submissionsLoading);
  const currentFile = useSelector((state) => state.dashboard.currentFile);

  const [showDelete, setShowDelete] = useState(false);
  const [listPopupActive, setListPopupActive] = useState(false);
  const [allSubmissionsActive, setAllSubmissionsActive] = useState("all");
  const [freeRunActive, setFreeRunActive] = useState(false);

  //SET QUERY FOR GETTING DATA ID
  let query = useQuery();

  useEffect(() => {
    dispatch(licenceCheckRequest());
    dispatch(RequestVms());
    dispatch(RequestBackendServiceConnected());
  }, [dispatch]);

  useEffect(() => {
    const fetchData = () => {
      dispatch(RequestDailySubmissions());
      dispatch(RequestAllSubmissions());
      dispatch(RequestUniqueSubmissionsCount());
      dispatch(RequestIntegrationSubmissionCount("all"));
      dispatch(RequestDailyIntegrationSubmissionCount("all"));
      dispatch(RequestCleanSubmissionsState(allSubmissionsActive));
      dispatch(RequestSuspiciousSubmissionsState(allSubmissionsActive));
      dispatch(RequestMaliciousSubmissionsState(allSubmissionsActive));
      dispatch(
        RequestIntegrationSubmissionState({
          timeFilter: allSubmissionsActive,
          integrationType: "all",
        }),
      );
      dispatch(RequestAptCount());
      dispatch(RequestMalwareNameCounts(allSubmissionsActive));
      dispatch(
        RequestIntegrationMalwareNameCounts({
          timeFilter: allSubmissionsActive,
          integrationType: "all",
        }),
      );
    };

    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 28000);

    return () => clearInterval(interval);
  }, [dispatch, allSubmissionsActive]);

  useEffect(() => {
    if (query.get("data") !== null) {
      setListPopupActive(true);
    } else {
      setListPopupActive(false);
    }
  }, [query]);

  return (
    <div>
      <div className="dashboard">
        <div className="dashboard__header">
          <h1>MA360° Core</h1>
        </div>
        <div className="dashboard__body">
          <div className="dashboard__body__item">
            <FileCard />
          </div>
          <div className="dashboard__body__item">
            <MalwareDistribution
              allSubmissionsCount={allSubmissionsCount}
              uniqueSubmissionsCount={uniqueSubmissionsCount}
              cleanSubmissionsState={selectState(
                allSubmissionsActive,
                submissionsCleanState,
                integrationsCleanState,
              )}
              suspiciousSubmissionsState={selectState(
                allSubmissionsActive,
                submissionsSuspiciousState,
                integrationsSuspiciousState,
              )}
              maliciousSubmissionsState={selectState(
                allSubmissionsActive,
                submissionsMaliciousState,
                integrationsMaliciousState,
              )}
              cleanSubmissionsCount={selectState(
                allSubmissionsActive,
                submissionsCleanCount,
                integrationsCleanCount,
              )}
              suspiciousSubmissionsCount={selectState(
                allSubmissionsActive,
                submissionsSuspiciousCount,
                integrationsSuspiciousCount,
              )}
              maliciousSubmissionsCount={selectState(
                allSubmissionsActive,
                submissionsMaliciousCount,
                integrationsMaliciousCount,
              )}
              allSubmissionsActive={allSubmissionsActive}
              setAllSubmissionsActive={setAllSubmissionsActive}
              aptCount={aptCount}
            />
            <div className="dashboard__body__item-card">
              <Card
                onClick={() => setFreeRunActive(true)}
                title="Free Run"
                icon={<MenIcon width={18} height={17} color={"white"} />}
                iconBackground="#E6A355"
              />
              <Card
                onClick={() => dailySubmissionsCount !== 0 && setAllSubmissionsActive("all_daily")}
                title="Daily Submissions"
                style={{ cursor: "pointer" }}
                value={dailySubmissionsCount}
                sideValue={
                  dailySubmissionsCount !== 0
                    ? `APT %${((dailyAptCount / dailySubmissionsCount) * 100).toFixed(0)}`
                    : "APT %0"
                }
                selected={allSubmissionsActive === "all_daily" ? true : false}
                icon={<ChartLineIcon width={18} height={17} color={"white"} />}
              />
              <Card
                onClick={() =>
                  integrationSubmissionsCount !== 0 && setAllSubmissionsActive("integrations")
                }
                title="Integrated Submissions"
                value={integrationSubmissionsCount}
                sideValue={`TODAY ${dailyIntegrationSubmissionsCount}`}
                selected={allSubmissionsActive === "integrations" ? true : false}
                icon={<ChartLineIcon width={18} height={17} color={"white"} />}
                iconBackground="#E6A355"
              />
            </div>
          </div>
          <div className="dashboard__body__item">
            <MalwareStats activeMenu={allSubmissionsActive} />
          </div>
          <DashBoardTable />
          <div className="dashboard__body__services-container">
            <SystemHealth />
            <ContainerServices />
          </div>
        </div>
      </div>
      <ListPopup
        onClickOk={() => setShowDelete(false)}
        show={listPopupActive}
        groupID={query.get("data")}
        tableType={query.get("table")}
        tableType2="dashboard"
      />
      <DemoPopup
        onClickOk={() => setShowDelete(false)}
        show={showDelete}
        setShow={setShowDelete}
        text="This feature is not available in the demo version."
        article="DEMO"
      />
      <Backdrop
        absolute={false}
        show={submissionsLoading}
        message={`Uploading file "${currentFile}", please wait...`}
      />
      <FreeRun active={freeRunActive} setActive={setFreeRunActive} />
    </div>
  );
};
export default Dashboard;
